import React, { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import { inputClass } from "@/lib/formStyles";
import { registerToQueue } from "../../services/queue";
import { hm, TodayAppointmentApi } from "../../services/queue/types";
import { REASON_NOTE_MAX_LENGTH } from "../../services/visitReasons/types";
import useVisitReasonsLookup from "../../hooks/queries/useVisitReasonsLookup";
import useDoctors from "../../hooks/queries/useDoctors";
import VisitReasonPicker from "../VisitReason/VisitReasonPicker";
import ModalUI from "../legacy/Modal";
import PatientSearchStep from "./PatientSearchStep";
import { DoctorSelect, QueueModalActions, QueueModalTitle } from "./QueueModalParts";

export type QueueCandidate = {
  id?: string | number;
  firstName?: string;
  lastName?: string;
};

type AddToQueueModalProps = {
  isOpen: boolean;
  /** Patient déjà choisi (page Patients). Absent : la fenêtre commence par la recherche. */
  patient?: QueueCandidate | null;
  appointmentsByPatient: Map<number, TodayAppointmentApi>;
  /** Patients déjà dans la file du jour (étape de recherche). */
  registeredPatientIds?: Set<number>;
  onClose: () => void;
};

const NO_REGISTERED_PATIENTS = new Set<number>();

/** Médecin du RDV du jour, au format de la valeur du `<select>`. */
const appointmentDoctorId = (appointment?: TodayAppointmentApi): string =>
  appointment?.doctorId != null ? String(appointment.doctorId) : "";

/**
 * Ajout d'un patient à la liste d'attente (DEC-8) : patient (recherche si non
 * fourni), motif obligatoire, précision libre, médecin pré-rempli depuis le RDV
 * du jour. Remonter le composant (prop `key`) pour repartir d'un formulaire vide.
 */
const AddToQueueModal: React.FC<AddToQueueModalProps> = ({
  isOpen,
  patient: providedPatient,
  appointmentsByPatient,
  registeredPatientIds = NO_REGISTERED_PATIENTS,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const [patient, setPatient] = useState<QueueCandidate | null>(providedPatient ?? null);
  const appointment = patient ? appointmentsByPatient.get(Number(patient.id)) : undefined;
  /**
   * `null` tant que la secrétaire n'a pas choisi : le médecin suit alors le RDV,
   * y compris quand la liste des RDV arrive après l'ouverture de la fenêtre.
   */
  const [chosenDoctorId, setChosenDoctorId] = useState<string | null>(null);
  const doctorId = chosenDoctorId ?? appointmentDoctorId(appointment);
  const [visitReasonId, setVisitReasonId] = useState<number | null>(null);
  const [reasonNote, setReasonNote] = useState("");

  const doctors = useDoctors(isOpen);
  const { data: visitReasons = [] } = useVisitReasonsLookup();

  const selectPatient = (candidate: QueueCandidate) => {
    setPatient(candidate);
    setChosenDoctorId(null);
  };

  const { mutate: register, isLoading } = useMutation(
    () =>
      registerToQueue({
        patientId: Number(patient?.id),
        doctorId: Number(doctorId),
        visitReasonId,
        reasonNote: reasonNote.trim() || null,
      }),
    {
      onSuccess: (result: any) => {
        if (!result?.ok) return;
        toast.success("Patient ajouté à la liste d'attente");
        queryClient.invalidateQueries(["queue-today-list"]);
        onClose();
      },
    }
  );

  const canSubmit = patient != null && Boolean(doctorId) && visitReasonId != null && !isLoading;

  return (
    <ModalUI
      isOpen={isOpen}
      onClose={() => {
        if (!isLoading) onClose();
      }}
      styles={{ width: "min(580px, 92vw)", padding: 0 }}
    >
      <div className="flex flex-col">
        <div className="space-y-5 px-6 pt-6">
          <QueueModalTitle eyebrow="Liste d'attente" title="Ajouter un patient" />

          {patient == null ? (
            <PatientSearchStep
              appointmentsByPatient={appointmentsByPatient}
              registeredPatientIds={registeredPatientIds}
              onSelect={selectPatient}
            />
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-800">
                    {patient.firstName} <span className="uppercase">{patient.lastName}</span>
                  </p>
                  <p className="text-xs text-slate-500">
                    {appointment
                      ? `RDV ${hm(appointment.appointmentTime)} · médecin pré-rempli`
                      : "Sans RDV aujourd'hui"}
                  </p>
                </div>
                {!providedPatient && (
                  <button
                    type="button"
                    onClick={() => setPatient(null)}
                    disabled={isLoading}
                    className="shrink-0 text-xs font-medium text-emerald-700 hover:text-emerald-800"
                  >
                    Changer de patient
                  </button>
                )}
              </div>

              <div>
                <span className="mb-2 block text-sm font-medium text-slate-700">Motif de visite</span>
                <VisitReasonPicker
                  reasons={visitReasons}
                  value={visitReasonId}
                  onChange={setVisitReasonId}
                  disabled={isLoading}
                />
              </div>

              <label className="block text-sm font-medium text-slate-700">
                Précision <span className="font-normal text-slate-400">(facultatif)</span>
                <input
                  type="text"
                  className={inputClass}
                  value={reasonNote}
                  maxLength={REASON_NOTE_MAX_LENGTH}
                  placeholder="Ex. OCT œil droit, contrôle à 1 mois…"
                  onChange={(event) => setReasonNote(event.target.value)}
                />
              </label>

              <DoctorSelect
                doctors={doctors}
                value={doctorId}
                onChange={setChosenDoctorId}
                placeholder="— Choisir un médecin —"
                disabled={isLoading}
              />
            </>
          )}
        </div>

        <QueueModalActions
          onCancel={onClose}
          onConfirm={() => register()}
          confirmLabel="Ajouter à la liste"
          confirmIcon="bx-list-plus"
          confirmDisabled={!canSubmit}
          isLoading={isLoading}
        />
      </div>
    </ModalUI>
  );
};

export default AddToQueueModal;
